//@flow
import * as Immutable from 'immutable';

import { TemperatureThreshold } from 'common/models/flow/TemperatureThreshold';
import type { TemperatureThresholdModelType } from 'common/models/flow/TemperatureThreshold';

//Validation stuff
import {
    validate,
    validatable,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import { NumberUtils } from 'common/utils/index';
import DataUtils from 'common/utils/DataUtils';
import { Units } from 'common/enums/UnitEnum';

import I18nHelper from 'common/helpers/I18nHelper';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

export const TemperatureType = {
    Chilled: 'CHILLED',
    Frozen: 'FROZEN'
};

const translationsNamespace = ['validations', 'entityManagement'];

const nsTranslate = I18nHelper.translate.bind(null, translationsNamespace);

const preferredUnitsForTemp = [
    Units.list.temperature.DegreeCelsius,
    Units.list.temperature.DegreeFahrenheit
];
const sourceUnitForTemp = Units.list.temperature.DegreeCelsius;

@validatable
class InternalTemperatureThreshold extends TemperatureThreshold {
    static fromJS(
        json: TemperatureThresholdModelType
    ): InternalTemperatureThreshold {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @validate
    setLowerThreshold(lowerThreshold: number): this {
        return super.setLowerThreshold(lowerThreshold);
    }

    validateLowerThreshold() {
        if (DataUtils.isEmpty(this.lowerThreshold)) {
            return;
        }
        if (!NumberUtils.isNumeric(this.lowerThreshold)) {
            return nsTranslate('skuValidationErrors.tempNotANumber');
        }
    }

    @validate
    setHigherThreshold(higherThreshold: number): this {
        return super.setHigherThreshold(higherThreshold);
    }

    validateHigherThreshold() {
        if (DataUtils.isEmpty(this.higherThreshold)) {
            return;
        }
        if (!NumberUtils.isNumeric(this.higherThreshold)) {
            return nsTranslate('skuValidationErrors.tempNotANumber');
        }
    }

    @validate
    setTemperatureType(temperatureType: string): this {
        return super.setTemperatureType(temperatureType);
    }

    validateTemperatureType() {
        let validationMessage = null;
        const temperatureTypes = Object.values(TemperatureType);
        if (
            this.temperatureType?.trim() &&
            !temperatureTypes.includes(this.temperatureType)
        ) {
            validationMessage = nsTranslate(
                'entityValidationErrors.typeEnumValidation',
                {
                    typeList: temperatureTypes.join(', '),
                    entityNameId: 'temperature'
                }
            );
        }
        return validationMessage;
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    static getPreferredUnitForTemp(unitConverter: UnitConversionHelper) {
        return unitConverter.getPreferredUnit({
            preferredUnits: preferredUnitsForTemp,
            sourceUnit: sourceUnitForTemp
        });
    }

    static getPreferredUnitTextForTemp(unitConverter: UnitConversionHelper) {
        return unitConverter.getConvertedUnitDisplayText({
            preferredUnits: preferredUnitsForTemp,
            sourceUnit: sourceUnitForTemp,
            pluralizeUnit: false
        });
    }

    convertToPreferredUnit(unitConverter: UnitConversionHelper): this {
        let updatedTempThreshold = this;
        const convertTemp = value =>
            unitConverter.convertValue({
                value: NumberUtils.toNumber(value),
                sourceUnit: sourceUnitForTemp,
                preferredUnits: preferredUnitsForTemp
            }).value;
        const { lowerThreshold, higherThreshold } = this;
        if (NumberUtils.isNumeric(lowerThreshold)) {
            updatedTempThreshold = updatedTempThreshold.setLowerThreshold(
                convertTemp(lowerThreshold)
            );
        }
        if (NumberUtils.isNumeric(higherThreshold)) {
            updatedTempThreshold = updatedTempThreshold.setHigherThreshold(
                convertTemp(higherThreshold)
            );
        }
        return updatedTempThreshold;
    }

    convertToSourceUnit(unitConverter: UnitConversionHelper): this {
        let updatedTempThreshold = this;
        const preferredUnit = InternalTemperatureThreshold.getPreferredUnitForTemp(
            unitConverter
        );
        const convertTemp = value =>
            unitConverter.basicConvert({
                value: NumberUtils.toNumber(value),
                sourceUnit: preferredUnit,
                targetUnit: sourceUnitForTemp
            });
        const { lowerThreshold, higherThreshold } = this;
        if (NumberUtils.isNumeric(lowerThreshold)) {
            updatedTempThreshold = updatedTempThreshold.setLowerThreshold(
                convertTemp(lowerThreshold)
            );
        }
        if (NumberUtils.isNumeric(higherThreshold)) {
            updatedTempThreshold = updatedTempThreshold.setHigherThreshold(
                convertTemp(higherThreshold)
            );
        }
        return updatedTempThreshold;
    }
}

export { InternalTemperatureThreshold as TemperatureThreshold };
